
import React, { useState, useEffect } from 'react';
import { db, auth } from '../services/firebase';
import { collection, onSnapshot, addDoc, query, orderBy, limit, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { UserRole } from '../types';

const NotificationManagement: React.FC = () => {
    const [notes, setNotes] = useState<any[]>([]);
    const [title, setTitle] = useState('');
    const [message, setMessage] = useState('');
    const [canPost, setCanPost] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const checkRole = async () => {
            if (auth.currentUser) {
                const userRef = doc(db, 'users', auth.currentUser.uid);
                const userSnap = await getDoc(userRef);
                if (userSnap.exists()) {
                    const role = userSnap.data().role;
                    setCanPost(role === UserRole.ADMIN || role === UserRole.STAFF);
                }
            }
            setLoading(false);
        };
        checkRole();

        const q = query(collection(db, 'notifications'), orderBy('createdAt', 'desc'), limit(15));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setNotes(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        });
        
        return () => unsubscribe();
    }, []);

    const handleSend = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canPost) return alert("Unauthorized access.");
        
        const notificationData = { 
            title, 
            message, 
            createdAt: Date.now(), 
            role: 'all',
            senderName: auth.currentUser?.displayName || 'System',
            type: 'push_broadcast' // Explicitly marking as a push broadcast
        };

        try {
            // This write to Firebase Firestore triggers the 'onSnapshot' listener 
            // in every connected user's App.tsx, which then shows the Push Notification.
            await addDoc(collection(db, 'notifications'), notificationData);
            
            setTitle(''); 
            setMessage('');
            alert("Announcement broadcasted successfully to all devices via Firebase Push!");
        } catch (err) {
            console.error("Error broadcasting:", err);
            alert("Failed to broadcast notification.");
        }
    };

    if (loading) return <div className="p-8 text-center text-gray-500">Verifying permissions...</div>;
    if (!canPost) return <div className="p-10 text-center text-red-500 font-bold">You do not have permission to post announcements.</div>;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-black dark:text-white tracking-tight">Global Announcements</h2>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1">Firebase Push Notification Center</p>
                </div>
                <div className="flex items-center gap-2 px-4 py-2 bg-green-50 dark:bg-green-900/20 rounded-full">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                    <span className="text-[10px] font-black text-green-600 uppercase tracking-widest">Push Server Active</span>
                </div>
            </div>

            <form onSubmit={handleSend} className="bg-white dark:bg-dark-card p-8 rounded-[2.5rem] border border-gray-100 dark:border-gray-800 space-y-6 shadow-xl">
                <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-gray-800 flex items-center justify-center text-blue-600">
                        <i className="fas fa-paper-plane"></i>
                    </div>
                    <div>
                        <h3 className="text-lg font-black dark:text-white">New Broadcast</h3>
                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Linked to Firebase Cloud Sync</p>
                    </div>
                </div>
                
                <div className="space-y-4">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Headline</label>
                        <input 
                            required 
                            placeholder="e.g. Emergency Holiday Announcement" 
                            className="w-full bg-gray-50 dark:bg-gray-800 px-6 py-4 rounded-2xl dark:text-white font-bold outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white transition-all border border-transparent" 
                            value={title} 
                            onChange={e => setTitle(e.target.value)} 
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Detailed Message</label>
                        <textarea 
                            required 
                            placeholder="Type the announcement details here..." 
                            className="w-full bg-gray-50 dark:bg-gray-800 px-6 py-4 rounded-2xl dark:text-white font-bold outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white transition-all border border-transparent min-h-[120px] resize-none" 
                            value={message} 
                            onChange={e => setMessage(e.target.value)} 
                        />
                    </div>
                </div>

                <div className="pt-4 flex flex-col md:flex-row gap-4 items-center">
                    <button 
                        type="submit" 
                        className="w-full md:w-auto px-10 py-5 bg-blue-600 text-white font-black rounded-2xl shadow-xl shadow-blue-200 dark:shadow-none hover:bg-blue-700 transition-all active:scale-95 flex items-center justify-center gap-3"
                    >
                        <i className="fas fa-broadcast-tower"></i>
                        Broadcast Push Notification
                    </button>
                    <div className="flex items-center gap-2 text-[10px] text-gray-400 font-bold uppercase">
                        <i className="fas fa-info-circle"></i>
                        <span>Sends to all registered devices via Firebase</span>
                    </div>
                </div>
            </form>
            
            <div className="space-y-4 mt-12">
                <div className="flex items-center gap-2 px-1">
                    <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">Push History</h3>
                    <div className="flex-1 h-px bg-gray-100 dark:bg-gray-800"></div>
                </div>
                {notes.length === 0 ? (
                    <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-[2rem] border-2 border-dashed border-gray-200 dark:border-gray-700">
                        <i className="fas fa-history text-3xl text-gray-300 mb-3"></i>
                        <p className="text-sm text-gray-400 font-bold uppercase tracking-widest">No broadcasts found</p>
                    </div>
                ) : (
                    notes.map(n => (
                        <div key={n.id} className="bg-white dark:bg-dark-card p-6 rounded-[2rem] border border-gray-100 dark:border-gray-800 shadow-sm flex justify-between items-start group hover:shadow-md transition-all">
                            <div className="flex gap-5">
                                <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-600 shrink-0">
                                    <i className="fas fa-bullhorn text-lg"></i>
                                </div>
                                <div>
                                    <div className="flex items-center gap-2 mb-1">
                                        <h4 className="font-black dark:text-white text-base">{n.title}</h4>
                                        <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 text-[8px] font-black uppercase rounded-md tracking-tighter">Push Sent</span>
                                    </div>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed max-w-2xl">{n.message}</p>
                                    <div className="flex items-center gap-3 mt-3">
                                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight flex items-center gap-1">
                                            <i className="fas fa-user-circle text-[12px]"></i>
                                            {n.senderName}
                                        </p>
                                        <span className="text-gray-200 dark:text-gray-700">•</span>
                                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight flex items-center gap-1">
                                            <i className="fas fa-clock text-[12px]"></i>
                                            {new Date(n.createdAt).toLocaleString()}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            <button 
                                onClick={() => {
                                    if(confirm("Are you sure you want to delete this broadcast?")) {
                                        deleteDoc(doc(db, 'notifications', n.id));
                                    }
                                }} 
                                className="text-red-400 p-3 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 dark:hover:bg-red-900/10 rounded-xl"
                            >
                                <i className="fas fa-trash-alt"></i>
                            </button>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default NotificationManagement;
